import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import ReactDOM from 'react-dom';

var ModalContext = React.createContext({
    showModal: function () { return undefined; },
    hideModal: function () { return undefined; }
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var ModalRoot = function (_a) {
    var ModalComponent = _a.modalComponent, modalProps = _a.modalProps, rootComponent = _a.rootComponent;
    var _b = useState(), root = _b[0], setRoot = _b[1];
    useEffect(function () { return setRoot(rootComponent || document.body); }, []);
    if (root && ModalComponent) {
        return (ReactDOM.createPortal(React.createElement(ModalComponent, __assign({}, modalProps)), root));
    }
    return null;
};

var ModalProvider = function (_a) {
    var children = _a.children, rootComponent = _a.rootComponent;
    var _b = useState({}), modal = _b[0], setModal = _b[1];
    var showModal = useCallback(function (component, props) { return (setModal({ component: component, props: props })); }, []);
    var hideModal = useCallback(function () { return (setModal({})); }, []);
    var value = useMemo(function () { return ({ showModal: showModal, hideModal: hideModal }); }, []);
    return (React.createElement(ModalContext.Provider, { value: value },
        React.createElement(React.Fragment, null,
            children,
            React.createElement(ModalRoot, { modalComponent: modal.component, modalProps: modal.props, rootComponent: rootComponent }))));
};

var useModal = function (component, inputs) {
    if (inputs === void 0) { inputs = []; }
    var context = useContext(ModalContext);
    var _a = useState(false), isOpen = _a[0], setIsOpen = _a[1];
    var _b = useState({}), props = _b[0], setProps = _b[1];
    var modal = useMemo(function () { return component; }, inputs);
    var hideModal = useCallback(function () { return setIsOpen(false); }, []);
    var showModal = useCallback(function (props) {
        setIsOpen(true);
        setProps(props || {});
    }, []);
    useEffect(function () {
        if (isOpen) {
            context.showModal(modal, props);
        }
        else {
            context.hideModal();
        }
        return function () { return context.hideModal(); };
    }, [modal, isOpen]);
    return [showModal, hideModal];
};

export default useModal;
export { ModalContext, ModalProvider };
//# sourceMappingURL=index.es.js.map
